<template>
    <div class="card" style="min-height: calc(100vh - 180px)">
        <div class="bg-blue-light p-1">
            <TitleButton
                btnTitle="Add New"
                :showBtn="false"
                :showAddNew="false"
                :title="pageTitle"
            />
        </div>

        <div class="my-2 px-2">
            <DateQuerySetter
                class="filter-row"
                btnTitle="Go"
                @onClickGo="resetPageAndFetch"
            >
                <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                    <label for="bill-number" class="col-form-label">Bill Number: </label>
                    <input
                      id="bill-number"
                      class="form-control"
                      v-model="billNumber"
                      @keyup="resetPageAndFetch(true)"
                      placeholder="Search by bill number"
                    />
                </div>
                <div class="col-auto d-flex align-items-end mt-2">
                    <FilterPopover
                        ref="filterPopoverRef"
                        title="Filters"
                        :active-count="activeFilterCount"
                        @open="onOpenFilter"
                        @apply="onApplyFilters"
                        @clear="onClearFilters"
                    >
                        <div class="col-12" v-if="!isRoutePharmacyBillReturn">
                            <label class="filter-label">Bill Type</label>
                            <v-select
                                placeholder="Select Bill Type"
                                v-model="draftFilters.billType"
                                :options="billTypeList"
                                label="label"
                                :reduce="label => label.value"
                            />
                        </div>
                        <div class="col-12">
                            <label class="filter-label">Name</label>
                            <AsyncSelect
                                placeholder="Name, ID, Mobile No"
                                v-model="draftFilters.patient"
                                :api-service="fetchContactProfiles"
                                :additional-query="additionalQuery"
                                :format-label="formatPatientLabel"
                            />
                        </div>
                    </FilterPopover>
                </div>
            </DateQuerySetter>
        </div>
        <div class="col-12 px-2 mt-2">
            <BillManagerListTable :salesList="salesList" :billTypeList="billTypeList" :billType="appliedFilters.billType"/>
        </div>
        <div class="px-2"><Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination"/></div>
    </div>
</template>

<script setup>
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import FilterPopover from '@/components/atom/FilterPopover.vue'
import {computed, inject, onMounted, ref} from 'vue'
import {useStore} from 'vuex'
import {useRoute, useRouter} from 'vue-router'
import handlePurchase from '@/services/modules/purchase'
import TitleButton from '@/components/atom/TitleButton'
import Pagination from '@/components/atom/Pagination'
import handleContact from "@/services/modules/contact";
import BillManagerListTable from "@/components/molecule/company/hospital/billing/bill-manager/ListTable.vue";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import {useAsyncDropdownHelper} from "@/services/utils/asyncDropdownHelper";
import handleInventory from "@/services/modules/inventory";

const router = useRouter()
const store = useStore()
let offset = ref(10)
let salesList = ref([])
const billTypeList = ref([])
const billNumber = ref('')
const filterPopoverRef = ref(null)
const route = useRoute()
const showError =  inject('showError');

const {fetchSalesList} = handlePurchase()
const {fetchContactProfiles} = handleContact();
const {formatPatientLabel} = useAsyncDropdownHelper()
const {fetchHome} = handleInventory()

const additionalQuery = {
    roles: "patient,customer"
}

const companyId = computed(() => {
    return route.params.companyId
})
const startDate = computed(() => {
    return route.query.start
})
const endDate = computed(() => {
    return route.query.end
})
const page = computed(() => {
    return route.query.page
})

const isRoutePharmacyBillReturn = computed(() => {
    return route.name === 'pharmacy-bill-return-list';
})

const pageTitle = computed(() => {
    return route.name === 'pharmacy-bill-return-list' ? 'Pharmacy Bill Return' : 'Bill Manager List';
})

function getDefaultBillType() {
    if (isRoutePharmacyBillReturn.value) return 'pharmacy_invoice'
    return null
}

const getEmptyFilters = () => ({
    billType: getDefaultBillType(),
    patient: null
})

const appliedFilters = ref(getEmptyFilters())
const draftFilters = ref(getEmptyFilters())

const activeFilterCount = computed(() => {
    let count = 0
    if (appliedFilters.value.patient) count++
    if (isRoutePharmacyBillReturn.value) return count
    if (appliedFilters.value.billType) count++
    return count
})

function getQuery (billNumberSearch = false) {
    const filters = appliedFilters.value
    let query = '?company_id=' + companyId.value
    query += `&sale_type=${filters.billType}`
    if (filters.patient) query += '&contact_profile_id=' + filters.patient.id
    if (billNumber.value) query += '&bill_number=' + billNumber.value
    if (page.value) query += '&page=' + page.value
    query += '&offset=' + offset.value
    if (billNumberSearch) return query
    if (startDate.value) query += '&start_date=' + startDate.value
    if (endDate.value) query += '&end_date=' + endDate.value
    return query
}

function onOpenFilter() {
    draftFilters.value = Object.assign({}, appliedFilters.value)
}

async function applyAndFetch(filters) {
    appliedFilters.value = Object.assign({}, filters)
    store.commit('resetPagination')
    const routeQuery = Object.assign({}, route.query, {page: 1})
    routeQuery.sale_type = filters.billType
    await router.push({path: route.path, query: routeQuery})
    await onClickGo()
}

function onApplyFilters() {
    applyAndFetch(draftFilters.value)
}

function onClearFilters() {
    draftFilters.value = getEmptyFilters()
    if (filterPopoverRef.value) filterPopoverRef.value.close()
    applyAndFetch(getEmptyFilters())
}

async function resetPageAndFetch(billNumberSearch = false) {
    store.commit('resetPagination')
    const routeQuery = Object.assign({}, route.query, {page: 1})
    await router.push({path: route.path, query: routeQuery})
    await onClickGo(billNumberSearch)
}

async function onClickGo(billNumberSearch = false) {
    const res = await fetchSalesList(getQuery(billNumberSearch))
    if (!res.status) {
        salesList.value = []
        store.commit('resetPagination')
        return
    }
    salesList.value = res.date.data
    setPagination(res.date)
}

function setPagination(data) {
    store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    })
}

async function onPageChange (page) {
    let routeQuery = Object.assign({}, route.query)
    routeQuery.page = page
    await router.push({path: route.path, query: routeQuery})
    await onClickGo()
}

onMounted(() => {
    store.commit('resetPagination')
    fetchHome().then(res => {
        if (res.data) {
            for (const value of Object.keys(res.data.hospital_invoice_types)) {
                billTypeList.value.push({
                    value: value,
                    label: res.data.hospital_invoice_types[value]
                })
            }
        }

        if (isRoutePharmacyBillReturn.value) {
            onClickGo();
            return;
        }

        if (!route.query.sale_type) return

        appliedFilters.value.billType = route.query.sale_type;
        onClickGo();
    })
})

</script>

<style scoped>
@media (max-width: 767.98px) {
    .filter-row {
        position: relative;
    }
    .filter-row :deep(.filter-pop-wrap) {
        position: static;
    }
    .filter-row :deep(.filter-pop-wrap .filter-pop) {
        left: calc(var(--bs-gutter-x) * .5);
        right: auto;
        width: calc(100% - var(--bs-gutter-x)) !important;
        max-width: 520px;
    }
}
</style>
